import React, { useState, useEffect } from 'react';
import { auth, db } from './firebase';
import { GoogleAuthProvider, signInWithPopup, signOut } from 'firebase/auth';
import { collection, getDocs, doc, deleteDoc, updateDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { onAuthStateChanged } from 'firebase/auth';
import './styles/AdminPortal.css';

const AUTHORIZED_ADMIN_EMAILS = ["support@granitlabs.com", "akschool1920@gmail.com"];

export default function AdminPortal() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [authorized, setAuthorized] = useState(false);
  const [rsvps, setRsvps] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [newEntry, setNewEntry] = useState({
    parentName: '',
    email: '',
    phone: '',
    childNames: '',
    numberOfGuests: 1,
    reserveKids: 0,
    dietaryRestrictions: '',
    notes: ''
  });


  // CSV export
  const exportToCSV = () => {
    const headers = ['Parent Name', 'Email', 'Phone', 'Children', '# Guests', 'Reserve Kids', 'Non-Reserve Kids', 'Dietary Restrictions', 'Notes', 'Submitted'];
    const rows = filteredRsvps.map(rsvp => {
      const total = parseInt(rsvp.numberOfGuests) || 1;
      const reserve = parseInt(rsvp.reserveKids) || 0;
      return [
        rsvp.parentName || '',
        rsvp.email || '',
        rsvp.phone || '',
        rsvp.childNames || '',
        total,
        reserve,
        total - reserve,
        rsvp.dietaryRestrictions || '',
        rsvp.notes || '',
        formatTimestamp(rsvp.timestamp)
      ];
    });
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `party-rsvps-${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      setLoading(true);
      
      if (currentUser && !currentUser.isAnonymous) {
          setUser(currentUser);
          
          if (AUTHORIZED_ADMIN_EMAILS.includes(currentUser.email)) {
            setAuthorized(true);
          fetchRsvps();
          } else {
            await signOut(auth);
            setUser(null);
            setError("You are not authorized to access the admin portal.");
        }
      } else {
        setUser(null);
        setAuthorized(false);
      }
      
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const fetchRsvps = async () => {
    try {
      const snapshot = await getDocs(collection(db, 'rsvps'));
      const rsvpData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      // Sort by timestamp, newest first
      rsvpData.sort((a, b) => {
        const timeA = a.timestamp?.seconds || 0;
        const timeB = b.timestamp?.seconds || 0;
        return timeB - timeA;
      });
      
      setRsvps(rsvpData);
    } catch (error) {
      console.error("Error fetching RSVPs:", error);
      setError("Failed to load RSVPs. Please try again.");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this RSVP?')) return;
    
    try {
      await deleteDoc(doc(db, 'rsvps', id));
      setRsvps(prev => prev.filter(rsvp => rsvp.id !== id));
    } catch (error) {
      console.error("Error deleting RSVP:", error);
      setError("Failed to delete RSVP.");
    }
  };

  const handleReserveChange = async (id, reserveKids) => {
    const numReserve = parseInt(reserveKids) || 0;
    try {
      await updateDoc(doc(db, 'rsvps', id), { reserveKids: numReserve });
      setRsvps(prev => prev.map(rsvp => 
        rsvp.id === id ? { ...rsvp, reserveKids: numReserve } : rsvp
      ));
    } catch (error) {
      console.error("Error updating reserve count:", error);
      setError("Failed to update reserve count.");
    }
  };

  const handleAddEntry = async (e) => {
    e.preventDefault();
    if (!newEntry.parentName.trim() || !newEntry.childNames.trim()) {
      setError("Parent name and children names are required.");
      return;
    }
    
    try {
      const docData = {
        ...newEntry,
        numberOfGuests: parseInt(newEntry.numberOfGuests) || 1,
        reserveKids: parseInt(newEntry.reserveKids) || 0,
        timestamp: serverTimestamp(),
        manualEntry: true
      };
      
      const docRef = await addDoc(collection(db, 'rsvps'), docData);
      
      // Add to local state with the new ID
      setRsvps(prev => [{
        id: docRef.id,
        ...docData,
        timestamp: { seconds: Date.now() / 1000 }
      }, ...prev]);
      
      // Reset form and close modal
      setNewEntry({
        parentName: '',
        email: '',
        phone: '',
        childNames: '',
        numberOfGuests: 1,
        reserveKids: 0,
        dietaryRestrictions: '',
        notes: ''
      });
      setShowAddModal(false);
      setError(null);
    } catch (error) {
      console.error("Error adding entry:", error);
      setError("Failed to add entry.");
    }
  };

  const handleGoogleSignIn = async () => {
    try {
      setError(null);
      const provider = new GoogleAuthProvider();
      await signInWithPopup(auth, provider);
    } catch (error) {
      console.error("Error signing in:", error);
      setError("Failed to sign in. Please try again.");
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      setAuthorized(false);
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "Unknown";
    try {
      if (timestamp.seconds) {
        return new Date(timestamp.seconds * 1000).toLocaleString();
      }
      if (timestamp instanceof Date) {
        return timestamp.toLocaleString();
      }
        return new Date(timestamp).toLocaleString();
    } catch {
      return "Invalid date";
    }
  };

  // Filter RSVPs by search term
  const filteredRsvps = rsvps.filter(rsvp => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      (rsvp.parentName && rsvp.parentName.toLowerCase().includes(term)) ||
      (rsvp.email && rsvp.email.toLowerCase().includes(term)) ||
      (rsvp.childNames && rsvp.childNames.toLowerCase().includes(term)) ||
      (rsvp.phone && rsvp.phone.includes(term))
    );
  });

  // Calculate totals
  const totalGuests = filteredRsvps.reduce((sum, rsvp) => sum + (parseInt(rsvp.numberOfGuests) || 1), 0);
  const totalReserve = filteredRsvps.reduce((sum, rsvp) => sum + (parseInt(rsvp.reserveKids) || 0), 0);
  const totalNonReserve = totalGuests - totalReserve;
  const withDietary = filteredRsvps.filter(rsvp => rsvp.dietaryRestrictions).length;

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  if (authorized) {
    return (
      <div className="admin-portal">
        <header className="admin-header">
          <h1>Party RSVP Admin</h1>
          <div className="admin-user-info">
            <span>Logged in as {user.email}</span>
            <button onClick={handleSignOut} className="admin-logout-btn">Sign Out</button>
          </div>
        </header>
        
        <div className="admin-container">
          <div className="stats-bar">
            <div className="stat">
              <span className="stat-number">{filteredRsvps.length}</span>
              <span className="stat-label">RSVPs</span>
            </div>
            <div className="stat">
              <span className="stat-number">{totalGuests}</span>
              <span className="stat-label">Total Kids</span>
            </div>
            <div className="stat stat-reserve">
              <span className="stat-number">{totalReserve}</span>
              <span className="stat-label">Reserve Spots</span>
            </div>
            <div className="stat stat-nonreserve">
              <span className="stat-number">{totalNonReserve}</span>
              <span className="stat-label">Non-Reserve</span>
            </div>
            <div className="stat">
              <span className="stat-number">{withDietary}</span>
              <span className="stat-label">Dietary Notes</span>
            </div>
          </div>
                    
          <div className="controls-bar">
            <input 
              type="text" 
              placeholder="Search by name, email, or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            <button onClick={() => setShowAddModal(true)} className="add-btn">
              + Add Entry
            </button>
            <button onClick={exportToCSV} className="export-btn">
              Export CSV
            </button>
            <button onClick={fetchRsvps} className="refresh-btn">
              Refresh
            </button>
          </div>
          
          {showAddModal && (
            <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
              <div className="modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h2>Add Manual Entry</h2>
                  <button className="modal-close" onClick={() => setShowAddModal(false)}>&times;</button>
                </div>
                <form onSubmit={handleAddEntry} className="add-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>Parent Name *</label>
                      <input
                        type="text"
                        value={newEntry.parentName}
                        onChange={e => setNewEntry(prev => ({ ...prev, parentName: e.target.value }))}
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>Phone</label>
                      <input
                        type="tel"
                        value={newEntry.phone}
                        onChange={e => setNewEntry(prev => ({ ...prev, phone: e.target.value }))}
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={newEntry.email}
                      onChange={e => setNewEntry(prev => ({ ...prev, email: e.target.value }))}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Children's Names *</label>
                    <input
                      type="text"
                      value={newEntry.childNames}
                      onChange={e => setNewEntry(prev => ({ ...prev, childNames: e.target.value }))}
                      placeholder="e.g. Emma, Jack"
                      required
                    />
                  </div>
                  
                  <div className="form-row">
                    <div className="form-group">
                      <label># of Kids</label>
                      <input
                        type="number"
                        min="1"
                        value={newEntry.numberOfGuests}
                        onChange={e => setNewEntry(prev => ({ ...prev, numberOfGuests: e.target.value }))}
                      />
                    </div>
                    <div className="form-group">
                      <label>Reserve Spots</label>
                      <input
                        type="number"
                        min="0"
                        max={newEntry.numberOfGuests}
                        value={newEntry.reserveKids}
                        onChange={e => setNewEntry(prev => ({ ...prev, reserveKids: e.target.value }))}
                      />
                    </div>
                  </div>
                  
                  <div className="form-group">
                    <label>Dietary Restrictions</label>
                    <input
                      type="text"
                      value={newEntry.dietaryRestrictions}
                      onChange={e => setNewEntry(prev => ({ ...prev, dietaryRestrictions: e.target.value }))}
                    />
                  </div>
                  
                  <div className="form-group">
                    <label>Notes</label>
                    <textarea
                      value={newEntry.notes}
                      onChange={e => setNewEntry(prev => ({ ...prev, notes: e.target.value }))}
                      rows={3}
                    />
                  </div>
                  
                  <div className="form-actions">
                    <button type="button" className="cancel-btn" onClick={() => setShowAddModal(false)}>
                      Cancel
                    </button>
                    <button type="submit" className="submit-btn">
                      Add Entry
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
                    
          {error && <div className="admin-error">{error}</div>}

          {filteredRsvps.length === 0 ? (
            <p className="no-results">No RSVPs found.</p>
          ) : (
            <div className="rsvp-cards">
              {filteredRsvps.map(rsvp => (
                <div key={rsvp.id} className={`rsvp-card ${rsvp.manualEntry ? 'manual-entry' : ''}`}>
                  <div className="rsvp-card-header">
                    <h3>
                      {rsvp.parentName || 'No Name'}
                      {rsvp.manualEntry && <span className="manual-badge">Manual</span>}
                    </h3>
                    <span className="guest-count">{rsvp.numberOfGuests || 1} kid{(rsvp.numberOfGuests || 1) > 1 ? 's' : ''}</span>
                  </div>
                    
                  <div className="rsvp-card-body">
                    <div className="rsvp-field">
                      <label>Children:</label>
                      <span>{rsvp.childNames || 'Not specified'}</span>
                    </div>
                    
                    <div className="rsvp-field reserve-field">
                      <label>Reserve Spots:</label>
                      <div className="reserve-input-group">
                        <select
                          value={rsvp.reserveKids || 0}
                          onChange={(e) => handleReserveChange(rsvp.id, e.target.value)}
                          className="reserve-select"
                        >
                          {[...Array((parseInt(rsvp.numberOfGuests) || 1) + 1).keys()].map(n => (
                            <option key={n} value={n}>{n}</option>
                          ))}
                        </select>
                        <span className="reserve-breakdown">
                          of {rsvp.numberOfGuests || 1} ({(rsvp.numberOfGuests || 1) - (rsvp.reserveKids || 0)} non-reserve)
                        </span>
                      </div>
                    </div>
                    
                    <div className="rsvp-field">
                      <label>Email:</label>
                      <span><a href={`mailto:${rsvp.email}`}>{rsvp.email || 'N/A'}</a></span>
                    </div>
                    
                    <div className="rsvp-field">
                      <label>Phone:</label>
                      <span>{rsvp.phone || 'N/A'}</span>
                    </div>
                    
                    {rsvp.dietaryRestrictions && (
                      <div className="rsvp-field dietary">
                        <label>⚠️ Dietary:</label>
                        <span>{rsvp.dietaryRestrictions}</span>
                        </div>
                    )}
                    
                    {rsvp.notes && (
                      <div className="rsvp-field notes">
                        <label>Notes:</label>
                        <span>{rsvp.notes}</span>
                      </div>
                    )}
                                </div>
                  
                  <div className="rsvp-card-footer">
                    <span className="timestamp">{formatTimestamp(rsvp.timestamp)}</span>
                    <button 
                      onClick={() => handleDelete(rsvp.id)} 
                      className="delete-btn"
                      title="Delete RSVP"
                    >
                      🗑️
                    </button>
                </div>
              </div>
              ))}
                  </div>
          )}
        </div>
      </div>
    );
  }

  // Login Screen
  return (
    <div className="admin-login">
      <h1>🎈 Party Admin</h1>
      <p>Sign in to view RSVPs</p>
      
      {error && <div className="admin-error">{error}</div>}
      
      <button onClick={handleGoogleSignIn} className="google-sign-in">
        Sign in with Google
      </button>
    </div>
  );
} 
